"use client";
import { Track } from "@/src/lib/tracking";

import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { deviceDetector } from "@ospweb/sdk-ui";
import { useMemberUser } from "./useMemberUser";
import {
  clearPlaidConnectChainPending,
  readPlaidConnectChainPending,
  releasePlaidOAuthResume,
  savePlaidConnectChainPending,
  shouldResumePlaidConnectChain,
  tryAcquirePlaidOAuthResume,
} from "@/src/hooks/plaid/plaidConnectChainPending";
import { openPortfolioAccountsModal } from "@components/PlaidImportModal";
import {
  openPlaidConnectFlowModal,
  type PlaidAccountCategory,
} from "@components/PlaidConnectFlow";
import type {
  PlaidAccountTarget,
  PlaidBindSuccessContext,
} from "@components/PlaidImportModal/types";
import { favoritesCenter } from "@store/favorite/FavoritesCenter";
import { OnboardingPaywallModalCallable } from "@components/OnboardingWizard/Paywall/OnboardingPaywallModalCallable";
import { useMemoizedFn } from "ahooks";
import { OnboardingPaywallFreeTrialContentProps } from "@components/OnboardingWizard/Paywall/types";
import {
  usePlaidGateRemoteConfig,
  usePlaidGateStatus,
  shouldRequirePlaidConnectPaywall,
} from "@/src/hooks/plaid";
import {
  fetchPortfolioConnectionsQueryData,
  PORTFOLIO_CONNECTIONS_QUERY_KEY,
} from "@/src/hooks/plaid/portfolioConnectionsQuery";
import { useOspApiClientProvider } from "@/src/hooks/useOspApiClientProvider";
import type { SenderAttachment } from "@components/Sender/type";
import type { PlaidLinkTrackingContext } from "@/src/hooks/plaid/plaidLinkTracking";

export interface HandleImportClickParams {
  /**
   * 业务维度，透传到 Plaid 段的四条 `plaid_link_*` 事件
   * （见 `hooks/plaid/plaidLinkTracking.ts`）——「这次连接从哪个业务入口发起」。
   */
  plaidTrackingContext?: PlaidLinkTrackingContext;
  /**
   * Skips the manage modal and opens Plaid import directly.
   */
  forceFreshConnect?: boolean;
  /**
   * EDG-393 · 连接链的账户类型语境：透传 Plaid 弹窗与 My accounts
   * （分区过滤 + 绑定提示方向）。影响流程行为，非仅埋点。
   */
  accountTarget?: "banking" | "invest";
  /**
   * EDG-393 · bind ≠ Confirm：绑定成功后打开 V3.0.5 My accounts，仅当用户
   * 点 Confirm 才 resolve {action:"confirm"}。关闭 / 未确认 → {action:"close"}。
   */
  requireAccountsConfirm?: boolean;
  /** Per-call bind success hook（连接意图埋点等）；与 hook 级回调并行执行。 */
  onBindSuccess?: (ctx: PlaidBindSuccessContext) => void | Promise<void>;
  /** Per-call Paywall copy（如 Accounts Reconnect）；覆盖 hook 级 defaults */
  paywallCopyOverrides?: OnboardingPaywallFreeTrialContentProps["paywallCopyOverrides"];
  /** Per-call analytics source */
  analyticsSource?: string;
  /** Per-call tracking module */
  trackingModuleName?: string;
  /** 账户类型 (invest / bank) */
  accountType?: PlaidAccountCategory;
  /** 是否来自 Onboarding 流程 */
  fromOnboarding?: boolean;
  /** broker 列表 / 挽留页是否展示「Add manually」 */
  showAddManually?: boolean;
  /** 是否已选择/关联 Portfolio (用于退出挽留分流) */
  hasSelectedPortfolio?: boolean;
  /** 指定直连的 Broker ID */
  initialBrokerId?: number;
  /** 授权过期重新连接时的 account_id (仅 update mode 需要) */
  accountId?: string;
  /** 关联的 connection ID */
  connectionId?: string;
  /** 关闭回调 */
  onClose?: () => void;
  /** Per-call MANUAL_UPLOAD Confirm 回调；覆盖 hook 级 defaults */
  onUploadSuccess?: (attachments: SenderAttachment[]) => void | Promise<void>;
}

export interface UseHandleImportClickOptions {
  /** Runs after built-in folder refresh on Plaid bind success. */
  onBindSuccess?: (ctx: PlaidBindSuccessContext) => void | Promise<void>;
  /** Runs when user closes the Plaid import modal. */
  onComplete?: () => void;
  /** 关闭回调 */
  onClose?: () => void;
  /** MANUAL_UPLOAD Confirm 回调 */
  onUploadSuccess?: (attachments: SenderAttachment[]) => void | Promise<void>;
  paywallCopyOverrides?: OnboardingPaywallFreeTrialContentProps["paywallCopyOverrides"];
  accountType?: PlaidAccountCategory;
  fromOnboarding?: boolean;
  showAddManually?: boolean;
  hasSelectedPortfolio?: boolean;
  initialBrokerId?: number;
  accountId?: string;
}

/**
 * EDG-393 · 可等待的连接链结构化结果（fe-spec §3.4；修复轮 bind ≠ Confirm）。
 *
 * - `success`：Plaid **绑定成功**（bound）——只是技术结果，不等于用户确认；
 * - `confirm`：仅在 `requireAccountsConfirm` 链上，用户在 My accounts 点了
 *   Confirm 底钮 —— 唯一驱动 Post-link 的业务结果；
 * - `close`：用户关闭 / early-return（gate 加载中、门控失败、paywall 未付费、
 *   Plaid close/exit、My accounts 未确认关闭）；
 * - `error`：paywall 或 Plaid 弹窗抛错。
 *
 * 既有消费方全部 `void handleImportClick(...)`，返回值仍可忽略。
 */
export type HandleImportClickChainResult =
  | { action: "success"; confirm: false }
  | { action: "confirm"; confirm: true }
  | { action: "close"; confirm: false }
  | { action: "error"; confirm: false };

const CHAIN_EARLY_RETURN: HandleImportClickChainResult = {
  action: "close",
  confirm: false,
};

/**
 * EDG-393 review #19 防御上限：My accounts → add_account → Plaid → My accounts
 * 循环最多允许 MAX_ADD_ACCOUNT_ROUNDS 次 add_account 重连。正常用户行为
 * （连一次账户点一次 add）远达不到；仅当时序异常导致 openPortfolioAccountsModal
 * 立即 resolve add_account 时防止无限弹窗，超出后记 Track.error 并按 close 收尾。
 */
const MAX_ADD_ACCOUNT_ROUNDS = 5;

function resolvePlaidAccountType({
  accountType,
  accountTarget,
}: {
  accountType?: PlaidAccountCategory;
  accountTarget?: PlaidAccountTarget;
}): PlaidAccountCategory | undefined {
  if (accountType) {
    return accountType;
  }
  if (accountTarget === "invest") {
    return "invest";
  }
  if (accountTarget === "banking") {
    return "bank";
  }
  return undefined;
}

export interface UseHandleImportClickReturn {
  handleImportClick: (
    params?: HandleImportClickParams,
  ) => Promise<HandleImportClickChainResult>;
}

export const useHandleImportClick = (
  options?: UseHandleImportClickOptions,
): UseHandleImportClickReturn => {
  const {
    onBindSuccess,
    onComplete,
    onClose,
    paywallCopyOverrides,
    accountType,
    fromOnboarding,
    showAddManually,
    hasSelectedPortfolio,
    initialBrokerId,
    accountId,
    onUploadSuccess,
  } = options || {};
  const { data: memberPlanInfo, isLoading: isMemberLoading } = useMemberUser();
  const { mode: gateMode } = usePlaidGateRemoteConfig();
  const {
    data: gate,
    isLoading: isGateLoading,
    isError: isGateError,
  } = usePlaidGateStatus({ enabled: true });
  const queryClient = useQueryClient();
  const { memberApi } = useOspApiClientProvider();

  const refreshPortfolioConnections = useMemoizedFn(async () => {
    if (!memberApi) {
      return;
    }
    await queryClient.invalidateQueries({
      queryKey: PORTFOLIO_CONNECTIONS_QUERY_KEY,
    });
    await queryClient.fetchQuery({
      queryKey: PORTFOLIO_CONNECTIONS_QUERY_KEY,
      queryFn: () => fetchPortfolioConnectionsQueryData(memberApi),
    });
  });

  const handleImportClick = useMemoizedFn(
    async (
      params?: HandleImportClickParams,
    ): Promise<HandleImportClickChainResult> => {
      if (isMemberLoading || isGateLoading) {
        return CHAIN_EARLY_RETURN;
      }

      // S1.4：门控态读取失败 → 不放行也不弹墙
      if (isGateError || gate === undefined) {
        return CHAIN_EARLY_RETURN;
      }

      const isPaidMember = Boolean(memberPlanInfo?.isMember);
      const requirePaywall = shouldRequirePlaidConnectPaywall({
        isPaidMember,
        gateMode,
        plaidStatus: gate.plaidStatus,
      });

      if (requirePaywall) {
        try {
          const paywallPaid = await OnboardingPaywallModalCallable({
            isPC: !deviceDetector.isMobile(),
            analyticsSource: params?.analyticsSource ?? "portfolio_connect",
            trackingModuleName:
              params?.trackingModuleName ?? "portfolio_connect",
            paywallCopyOverrides:
              params?.paywallCopyOverrides ?? paywallCopyOverrides,
          });
          if (!paywallPaid) {
            return CHAIN_EARLY_RETURN;
          }
        } catch (error) {
          Track.error(error, {
            tag: "useHandleImportClick",
            msg: "Paywall modal error:",
          });
          return { action: "error", confirm: false };
        }
      }

      const shouldPersistConfirmChain = Boolean(params?.requireAccountsConfirm);

      try {
        const bindSuccessHandler = async (ctx: PlaidBindSuccessContext) => {
          await favoritesCenter.fetchUserFolders({ forceRefresh: true });
          await refreshPortfolioConnections();
          await onBindSuccess?.(ctx);
          await params?.onBindSuccess?.(ctx);
        };

        const onUploadCb = params?.onUploadSuccess ?? onUploadSuccess;

        const openPlaidFlow = async (
          skipDenseWall = false,
        ): Promise<PlaidBindSuccessContext | null> => {
          let boundCtx: PlaidBindSuccessContext | null = null;

          if (shouldPersistConfirmChain) {
            savePlaidConnectChainPending(params ?? {});
          }

          await openPlaidConnectFlowModal({
            accountType: resolvePlaidAccountType({
              accountType: params?.accountType ?? accountType,
              accountTarget: params?.accountTarget,
            }),
            fromOnboarding: params?.fromOnboarding ?? fromOnboarding,
            showAddManually: params?.showAddManually ?? showAddManually,
            skipDenseWall,
            hasSelectedPortfolio:
              params?.hasSelectedPortfolio ?? hasSelectedPortfolio,
            initialBrokerId: params?.initialBrokerId ?? initialBrokerId,
            accountId: params?.accountId ?? accountId,
            connectionId: params?.connectionId,
            onUploadSuccess: onUploadCb,
            trackingContext: params?.plaidTrackingContext,
            onSuccess: async (res) => {
              if (!res.connectionId) {
                return;
              }
              const ctx: PlaidBindSuccessContext = {
                connectionId: res.connectionId,
                connection: res.connection,
              };
              boundCtx = ctx;
              await bindSuccessHandler(ctx);
            },
            onClose: () => {
              params?.onClose?.();
              onClose?.();
            },
          });

          return boundCtx;
        };

        let boundCtx: PlaidBindSuccessContext | null = await openPlaidFlow();

        if (!boundCtx) {
          onComplete?.();
          return CHAIN_EARLY_RETURN;
        }

        if (!params?.requireAccountsConfirm) {
          return { action: "success", confirm: false };
        }

        await refreshPortfolioConnections();

        let addAccountRounds = 0;
        for (;;) {
          const accountsResult = await openPortfolioAccountsModal({
            confirmEnabled: true,
            closeOnAddAccount: true,
            accountTarget: params?.accountTarget,
            boundConnection: boundCtx.connection,
            onBindSuccess: bindSuccessHandler,
          });
          if (accountsResult.action === "confirm") {
            onComplete?.();
            return { action: "confirm", confirm: true };
          }
          if (accountsResult.action === "add_account") {
            addAccountRounds += 1;
            if (addAccountRounds > MAX_ADD_ACCOUNT_ROUNDS) {
              // review #19：超出防御上限，按 close 收尾并留排查线索
              Track.error(
                new Error(
                  `My accounts add_account loop exceeded ${MAX_ADD_ACCOUNT_ROUNDS} rounds`,
                ),
                {
                  tag: "useHandleImportClick",
                  msg: "add_account chain aborted: max rounds exceeded",
                },
              );
              onComplete?.();
              return CHAIN_EARLY_RETURN;
            }
            boundCtx = await openPlaidFlow(true);
            if (!boundCtx) {
              onComplete?.();
              return CHAIN_EARLY_RETURN;
            }
            await refreshPortfolioConnections();
            continue;
          }
          onComplete?.();
          return CHAIN_EARLY_RETURN;
        }
      } catch (error) {
        Track.error(error, {
          tag: "useHandleImportClick",
          msg: "Plaid connect flow modal error:",
        });
        return { action: "error", confirm: false };
      } finally {
        if (shouldPersistConfirmChain) {
          clearPlaidConnectChainPending();
        }
      }
    },
  );

  useEffect(() => {
    if (
      !shouldResumePlaidConnectChain() ||
      isMemberLoading ||
      isGateLoading ||
      isGateError ||
      gate === undefined
    ) {
      return;
    }

    const pending = readPlaidConnectChainPending();
    if (!pending) {
      return;
    }

    if (!tryAcquirePlaidOAuthResume()) {
      return;
    }

    void handleImportClick(pending).finally(() => {
      releasePlaidOAuthResume();
    });
  }, [gate, handleImportClick, isGateError, isGateLoading, isMemberLoading]);

  return {
    handleImportClick,
  };
};
